import { Link } from '@tanstack/react-router';
import { useContext } from 'react';

import { cn } from '@/shared/lib/cn';

import { UsageContext } from './usage-context';

import type { BillingUsage } from '@mio/shared';

function dollars(amount: number) {
  if (amount > 0 && amount < 0.01) return '<$0.01';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function duration(seconds: number) {
  if (seconds <= 0) return '0 min';
  if (seconds < 60) return '<1 min';
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours}h${minutes % 60 ? ` ${minutes % 60}m` : ''}` : `${minutes} min`;
}

function date(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function BalanceMeter({
  label,
  remaining,
  limit,
  format,
}: {
  label: string;
  remaining: number;
  limit: number;
  format: (amount: number) => string;
}) {
  const available = Math.max(0, Math.min(limit, remaining));
  const percent = limit > 0 ? (available / limit) * 100 : 0;
  return (
    <div className="min-w-0 flex-1">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-2 text-xs">
        <span className="text-fg-muted">{label}</span>
        <span className="text-fg tabular-nums">{format(available)} left</span>
      </div>
      <div
        role="progressbar"
        aria-label={`${label} remaining`}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={available}
        aria-valuetext={`${format(available)} of ${format(limit)} remaining`}
        className="bg-line h-1 overflow-hidden rounded-sm"
      >
        <div
          className={cn('h-full transition-[width]', percent <= 10 ? 'bg-warning' : 'bg-accent')}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

/** One balance shared by every model feature; voice is a separate allowance. */
export function UsageBalance({
  usage: providedUsage,
  compact = false,
  showPlanLink = true,
  onManage,
  className,
}: {
  usage?: BillingUsage | null | undefined;
  compact?: boolean;
  showPlanLink?: boolean;
  onManage?: () => void;
  className?: string;
}) {
  const inheritedUsage = useContext(UsageContext);
  const usage = providedUsage === undefined ? inheritedUsage : providedUsage;
  if (!usage) return null;
  const trial = usage.tier !== 'paid';
  const exhausted = usage.ai.exhausted || usage.voice.exhausted;
  const resource = usage.ai.exhausted
    ? usage.voice.exhausted
      ? 'AI and voice allowances'
      : 'AI allowance'
    : 'Voice allowance';
  const renewal = usage.resetsAt ? `Renews ${date(usage.resetsAt)}.` : null;
  return (
    <section aria-label="AI and voice limits" className={cn('space-y-2', className)}>
      {!compact ? (
        <div>
          <h3 className="text-sm font-medium">Your shared allowances</h3>
          <p className="text-fg-muted mt-1 text-xs leading-5">
            {trial
              ? `${usage.tier === 'work_trial' ? '10-day work-email' : '3-day personal-email'} trial · Ends ${date(usage.periodEndsAt)}.`
              : `$50 AI credits and 4 hours of voice each month. ${renewal ?? `Current period ends ${date(usage.periodEndsAt)}.`}`}
          </p>
        </div>
      ) : null}
      <div className="flex items-center gap-4">
        <BalanceMeter
          label="AI credits"
          remaining={usage.ai.remainingUsd}
          limit={usage.ai.limitUsd}
          format={dollars}
        />
        <BalanceMeter
          label="Voice"
          remaining={usage.voice.remainingSeconds}
          limit={usage.voice.limitSeconds}
          format={duration}
        />
        {showPlanLink ? (
          <Link
            to="/account/plan"
            onClick={onManage}
            className="text-fg shrink-0 py-2 text-xs underline underline-offset-2"
            aria-label={
              trial && exhausted ? 'Upgrade to restore your allowances' : 'View limits and plans'
            }
          >
            {trial && exhausted ? 'Upgrade' : 'Limits & plans'}
          </Link>
        ) : null}
      </div>
      {exhausted ? (
        <p role="status" className="text-warning text-xs leading-5">
          {resource} used.{' '}
          {trial
            ? 'Choose a paid plan for $50 AI credits and 4 voice hours every month.'
            : (renewal ?? 'View your plan for your next allowance period.')}{' '}
          {!usage.ai.exhausted && usage.voice.exhausted ? 'You can continue in chat.' : null}
        </p>
      ) : null}
      {!compact ? (
        <>
          <p className="text-fg-muted text-xs leading-5">
            {dollars(usage.ai.usedUsd)} of {dollars(usage.ai.limitUsd)} AI credits used ·{' '}
            {duration(usage.voice.usedSeconds)} of {duration(usage.voice.limitSeconds)} voice used.
            {usage.ai.reservedUsd > 0 || usage.voice.reservedSeconds > 0
              ? ` In-progress requests reserve ${dollars(usage.ai.reservedUsd)} AI credits and ${duration(usage.voice.reservedSeconds)} voice. Completed requests return any unused reserved amount.`
              : null}
          </p>
          <p className="text-fg-muted text-xs leading-5">
            AI credits are shared across Workbench, Workshop, Mio and model tokens in Modules 3 and
            4. Voice time is shared across Mio and Workbench and counts listening time plus
            generated speech. Balances update while you work.
          </p>
          {trial ? (
            <p className="text-fg-muted text-xs leading-5">
              To continue after your trial or an allowance runs out, open Limits &amp; plans, choose
              Monthly or the 6-month pass, complete Stripe checkout, then return to continue where
              you left off. Both plans renew allowances every month.
            </p>
          ) : (
            <p className="text-fg-muted text-xs leading-5">
              Allowances renew monthly on both paid plans. If your allowance runs out, you can
              continue using your lessons while you wait for the next monthly renewal.
            </p>
          )}
        </>
      ) : null}
    </section>
  );
}
